/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getActivityExclusions,
    createActivityExclusion,
    deleteActivityExclusion,
    getActivityTickets,
} from "../activityService";
import {
    abAddBtn,
    abDeleteBtn,
    abItemCardShell,
    abListFieldInput,
} from "../activityBuilderUi";

const DeleteConfirmationModal = ({ isOpen, onCancel, onConfirm, title, message }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-6">
                    <div className="flex items-center space-x-3 text-red-600 mb-4">
                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                            <i className="fi fi-rr-triangle-warning text-xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm font-medium leading-relaxed">
                        {message}
                    </p>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <button
                        onClick={onCancel}
                        className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-white transition-all active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-8 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-95"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

const ExclusionSection = ({
    activityId,
    ticketId,
    title,
    isExpanded,
    onToggle,
}) => {
    const [text, setText] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
    
    const queryClient = useQueryClient();

    // Fetch exclusions specifically for this ticket
    const { data: exclusionsData, isLoading, refetch } = useQuery({
        queryKey: ["activityExclusions", activityId, ticketId],
        queryFn: () => getActivityExclusions(activityId, ticketId),
        enabled: !!activityId && !!ticketId,
    });

    const exclusions = exclusionsData?.data || [];

    const handleAdd = async () => {
        if (!text.trim()) return;
        setIsSaving(true);
        try {
            const newList = [...exclusions.map(i => i.exclusion), text.trim()];
            await createActivityExclusion(activityId, ticketId, newList);
            setText("");
            await refetch();
        } catch (error) {
            console.error("Failed to add exclusion", error);
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteModal.id) return;
        try {
            await deleteActivityExclusion(activityId, ticketId, deleteModal.id);
            setDeleteModal({ isOpen: false, id: null });
            await refetch();
        } catch (error) {
            console.error("Failed to delete exclusion", error);
        }
    };

    return (
        <>
            <div className={abItemCardShell}>
                {/* Header / Toggle */}
                <button 
                    onClick={onToggle}
                    className="w-full px-6 py-4 flex items-center justify-between bg-gray-50/50 hover:bg-gray-50 transition-colors border-b border-gray-100"
                >
                    <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isExpanded ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-gray-400'}`}>
                            <i className="fi fi-rr-ban"></i>
                        </div>
                        <div className="text-left">
                            <h4 className="text-base font-bold text-gray-900 tracking-tight">{title}</h4>
                            <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">
                                {isLoading ? 'Loading...' : `${exclusions.length} Exclusions`}
                            </p>
                        </div>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-white border border-gray-100 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                        <i className="fi fi-rr-angle-small-down text-gray-400"></i>
                    </div>
                </button>

                {/* Content Area */}
                {isExpanded && (
                    <div className="p-6 space-y-6 animate-in slide-in-from-top-2 duration-300">
                        {/* Full Width Input Section */}
                        <div className="space-y-2">
                            <label className="mb-1.5 block text-sm font-medium text-gray-700">Add new exclusion</label>
                            <div className="flex gap-3">
                                <input
                                    type="text"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={`Type exclusion for ${title}...`}
                                    className={abListFieldInput}
                                    onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                                />
                                <button
                                    type="button"
                                    onClick={handleAdd}
                                    disabled={isSaving || !text.trim()}
                                    className={`${abAddBtn} shrink-0 disabled:cursor-not-allowed disabled:opacity-50`}
                                >
                                    {isSaving ? "Saving..." : "Add"}
                                </button>
                            </div>
                        </div>

                        {/* Full Length List Section */}
                        <div className="space-y-2">
                            <label className="mb-1.5 block text-sm font-medium text-gray-700">Current exclusions</label>
                            <div className="space-y-2.5">
                                {isLoading ? (
                                    <div className="py-4 flex items-center justify-center text-gray-400 space-x-2">
                                        <i className="fi fi-rr-spinner animate-spin"></i>
                                        <span className="text-xs font-medium">Fetching details...</span>
                                    </div>
                                ) : exclusions.length === 0 ? (
                                    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-8 flex flex-col items-center justify-center text-gray-400">
                                        <i className="fi fi-rr-layers text-2xl mb-1 opacity-20"></i>
                                        <p className="text-xs italic">No items listed for this category</p>
                                    </div>
                                ) : (
                                    exclusions.map((item) => (
                                        <div 
                                            key={item.id} 
                                            className="w-full flex justify-between items-center bg-white hover:bg-gray-50 px-4 py-2 rounded-lg border border-gray-100 hover:border-gray-200 transition-all group"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className="w-2 h-2 rounded-full bg-primary/40 group-hover:bg-primary transition-colors"></div>
                                                <span className="text-sm text-gray-700 font-semibold">{item.exclusion}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setDeleteModal({ isOpen: true, id: item.id })}
                                                className={abDeleteBtn}
                                            >
                                                <i className="fi fi-rr-trash text-xs"></i>
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <DeleteConfirmationModal 
                isOpen={deleteModal.isOpen}
                onCancel={() => setDeleteModal({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Exclusion"
                message="Are you sure you want to delete this exclusion? This action cannot be undone."
            />
        </>
    );
};

const ActivityExclusion = ({ activityId, singleTicketId, singleTicketName }) => {
    // Fetch Tickets
    const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
        queryKey: ["activityTickets", activityId],
        queryFn: () => getActivityTickets(activityId),
        enabled: !!activityId && !singleTicketId,
    });
    const tickets = singleTicketId
        ? [{ id: singleTicketId, name: singleTicketName || "Ticket" }]
        : ticketsData?.data || [];
    const [expandedTicketId, setExpandedTicketId] = useState(null);

    if (!activityId) {
        return <div className="text-gray-500">Please save General Info first.</div>;
    }

    if (!singleTicketId && ticketsLoading) return (
        <div className="flex flex-col items-center justify-center p-20 text-gray-400">
            <i className="fi fi-rr-spinner animate-spin text-4xl mb-4"></i>
            <span className="font-bold uppercase tracking-widest text-[11px]">Building list...</span>
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-gray-100 bg-slate-50/40 p-5 space-y-4">
                <h4 className="text-sm font-bold text-gray-900">Ticket exclusions</h4>
                {tickets.length > 0 ? (
                    tickets.map((ticket, index) => {
                        const expanded =
                            expandedTicketId === null ? index === 0 : expandedTicketId === ticket.id;
                        return (
                        <ExclusionSection
                            key={ticket.id}
                            activityId={activityId}
                            ticketId={ticket.id}
                            title={ticket.name}
                            isExpanded={expanded}
                            onToggle={() =>
                                setExpandedTicketId((prev) =>
                                    prev === ticket.id ? null : ticket.id
                                )
                            }
                        />
                        );
                    })
                ) : (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-white py-8 text-center text-sm text-gray-500">
                        <i className="fi fi-rr-ticket-alt mb-2 inline-block text-2xl text-gray-300"></i>
                        <p className="font-medium text-gray-700">No custom tiers found</p>
                        <p className="mt-1 text-xs text-gray-500">Add tickets in the Tickets tab to manage exclusions here.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivityExclusion;
